import NextImage from 'next/image'
import { STATIC_IMAGE_BASE } from '@mc/constants'
import { ErrorBoundary } from '@mc/error-boundary'
import { Experiment } from '@mc/experiments-react'
import { useTranslation } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { usePromotion } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import { Locations } from '@mc/track-event'
import { TrackingContainer } from '@mc/visibility'
import CTA from 'components/CTA'
import {
  DefaultNameplate as DesktopDefaultNameplate,
  AlternativeNameplate,
} from 'components/HighContrastHero/components/HeroContent'
import Logo from 'components/Navigation/MCLogo/assets/logo.svg'
import {
  InstructorAccolades,
  PEN_6698_V1_CM_INSTRUCTOR_ACCOLADE_MODAL_FLAG_NAME,
  useCMInstructorAccoladeExperiment,
} from 'experiments/pen-6698_v1_cm_instructor_accolade_modal'
import {
  Pen7562HeadlineWrapper,
  Pen7562PricingLineWrapper,
} from 'experiments/pen-7562_v1_cm_promo_discount_guarantee'
import { Prcng1266PricingCtaSectionWrapper } from 'experiments/prcng-1266_v1_cm_dual_cta'
import { type PrunedCourse } from 'libs/server/api'
import { ClassPageLocations, trackPageSectionViewed } from '../../events'
import type { HeroData } from '../../types'
import { getCourseTrailerProps } from '../CourseTrailerModal'
import { getChaptersAndDuration, getNameplateLabel } from '../utils'
import { CardPricingLine } from './CardPricingLine'
import { DefaultNameplateMobile } from './DefaultNameplateMobile'
import { DiagonalBanner } from './DiagonalBanner'
import { TrailerAction } from './TrailerAction'
import classes from './Hero.module.scss'

const trackProps = {
  category: 'video-playlist-module',
  content_type: 'product',
  location: Locations.HERO,
}

export const CourseDataTrailerAction = () => {
  const { t } = useTranslation('@mc/next')
  const { course } = useStaticData<{
    course: PrunedCourse
  }>()
  const { chaptersCount, courseDuration } = getChaptersAndDuration(course)

  const trailer = getCourseTrailerProps(course)

  return (
    <div className='d-flex align-items-baseline justify-content-center flex-gap-2 mc-mt-4'>
      <p className='mc-text-small'>
        {chaptersCount >= 2 && (
          <>
            {t('course.hero.lessons', { lessonsCount: chaptersCount })}
            <span className='mc-opacity--muted'>{' · '}</span>
          </>
        )}
        <span className='mc-opacity--muted'>{courseDuration}</span>
      </p>
      <TrailerAction trailer={trailer} />
    </div>
  )
}

export const PricingCTASection = () => {
  const { t } = useTranslation('@mc/next')
  const { isPromotionOn } = usePromotion()

  // pen-6698_v1_cm_instructor_accolade_modal
  const { shouldShowCMInstructorAccoladeExp } =
    useCMInstructorAccoladeExperiment()

  return (
    <TrackingContainer
      onFirstVisible={() =>
        trackPageSectionViewed(ClassPageLocations.MEMBERSHIP_SECTION)
      }
      className={`mc-px-6 mc-py-5 mc-py-md-4 mc-mt-8 flex-shrink-0 mc-text--center position-relative mc-overflow--hidden ${classes.pricingContainer}`}
    >
      {isPromotionOn && <DiagonalBanner />}
      <NextImage
        src={`${STATIC_IMAGE_BASE}/CM_instructors_hero_2x.png`}
        alt=''
        width={183}
        height={38}
        className={classes.instructorAvatars}
      />
      <Pen7562HeadlineWrapper
        control={
          <p className='body mc-mt-2 mc-mt-md-1 mc-text--balance'>
            {t('course.hero.allClasses', {
              instructors: getNumberOfInstructors(),
            })}
          </p>
        }
      />
      <CTA fullWidth trackProps={trackProps} className='mc-mt-4 mc-mb-2' />
      <Pen7562PricingLineWrapper control={<CardPricingLine />} />
      {shouldShowCMInstructorAccoladeExp && (
        <Experiment
          name={PEN_6698_V1_CM_INSTRUCTOR_ACCOLADE_MODAL_FLAG_NAME}
          variant_1={() => <InstructorAccolades />}
        />
      )}
    </TrackingContainer>
  )
}

export const ContentDesktop = ({ data }: { data: HeroData }) => {
  const { course } = useStaticData<{
    course: PrunedCourse
  }>()

  return (
    <div className='container'>
      <div className='mc-text--center'>
        <div className='row justify-content-center'>
          <div className='col-9'>
            {data.isDynamicNameplate ? (
              <AlternativeNameplate
                nameplate={data.nameplate}
                header={data.header}
                showSeparator
              />
            ) : (
              <DesktopDefaultNameplate
                header={data.header}
                nameplate={data.nameplate || ''}
                namePlateSize={220}
                showSeparator
                nameplateAlt={getNameplateLabel(course)}
                aspectRatio={course.nameplateAspectRatio}
              />
            )}
          </div>
        </div>
        <div className='row d-flex justify-content-center no-gutters'>
          <div
            className='col-md-11 col-lg-10 mc-text-small'
            style={{ zIndex: 1 }}
          >
            <div className='mc-mt-3'>{data.subheader}</div>
          </div>
          <div className='col-12'>
            <CourseDataTrailerAction />
            <ErrorBoundary>
              <Prcng1266PricingCtaSectionWrapper
                Control={<PricingCTASection />}
              />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ContentMobile = ({ data }: { data: HeroData }) => (
  <div className='container'>
    <div className='row justify-content-between align-items-end'>
      <div className='col-12 d-flex flex-column align-items-start mc-text--left'>
        <Logo className='mc-icon mc-icon--3 mc-text-color--primary' />
        <DefaultNameplateMobile data={data} />
        <p className='mc-opacity--muted mc-text-small'>{data.subheader}</p>
        <CourseDataTrailerAction />
      </div>

      <ErrorBoundary>
        <Prcng1266PricingCtaSectionWrapper Control={<PricingCTASection />} />
      </ErrorBoundary>
    </div>
  </div>
)
